import { call, put, takeLatest } from 'redux-saga/effects';
import * as T from './loyaltyProgramTypes';
import * as actions from './loyaltyProgramActions';
import * as api from '../../../services/loyaltyProgramApi';
import { setLocations } from '../locations/loyaltyLocationsActions';
import type { StoreLocation } from '../../../components/loyalty/data/types';

/** Extract and map locations from a program API response. */
function mapApiLocations(data: any): StoreLocation[] {
  const apiLocations = data?.locations_summary?.locations;
  if (!Array.isArray(apiLocations) || apiLocations.length === 0) return [];
  return apiLocations.map((loc: any) => ({
    id: loc.location_id,
    name: loc.location_name || loc.location_id,
    status: (loc.is_location_active ? 'active' : 'closed') as 'active' | 'closed',
    city: loc.city,
    state: loc.state,
    accrual_enabled: loc.accrual_enabled ?? true,
  }));
}

function* fetchLoyaltyProgramSaga() {
  try {
    const response: any = yield call(api.getProgram);
    const data = response.data?.data ?? response.data;
    yield put(actions.fetchLoyaltyProgramSuccess(data));
    // Populate the locations slice so any page (e.g. Dashboard) can show a location filter
    const mapped = mapApiLocations(data);
    if (mapped.length > 0) yield put(setLocations(mapped));
  } catch (error: any) {
    yield put(actions.fetchLoyaltyProgramFailure(error.message || 'Failed to fetch program'));
  }
}

function* createLoyaltyProgramSaga(
  action: ReturnType<typeof actions.createLoyaltyProgramRequest>
) {
  try {
    const response: any = yield call(api.createProgram, action.payload);
    yield put(actions.createLoyaltyProgramSuccess(response.data?.data ?? response.data));
  } catch (error: any) {
    yield put(actions.createLoyaltyProgramFailure(error.message || 'Failed to create program'));
  }
}

function* updateLoyaltyProgramSaga(
  action: ReturnType<typeof actions.updateLoyaltyProgramRequest>
) {
  try {
    yield call(api.updateProgram, action.payload);
    // Re-fetch the full program so the form always syncs from a complete
    // GET response, not a potentially partial PUT response body.
    const getResponse: any = yield call(api.getProgram);
    const data = getResponse.data?.data ?? getResponse.data;
    yield put(actions.updateLoyaltyProgramSuccess(data));
    // Re-populate locations in case they changed after the update
    const mapped = mapApiLocations(data);
    if (mapped.length > 0) yield put(setLocations(mapped));
  } catch (error: any) {
    yield put(actions.updateLoyaltyProgramFailure(error.message || 'Failed to update program'));
  }
}

function* updateLoyaltyProgramStatusSaga(
  action: ReturnType<typeof actions.updateLoyaltyProgramStatusRequest>
) {
  try {
    yield call(api.updateProgramStatus, action.payload);
    // Re-fetch the full program so Redux always has the authoritative status
    // (the PATCH response may not return the full program object)
    const getResponse: any = yield call(api.getProgram);
    const data = getResponse.data?.data ?? getResponse.data;
    yield put(actions.updateLoyaltyProgramStatusSuccess(data));
  } catch (error: any) {
    yield put(
      actions.updateLoyaltyProgramStatusFailure(error.message || 'Failed to update status')
    );
  }
}


function* fetchLoyaltySpecialRewardsSaga() {
  try {
    const response: any = yield call(api.getSpecialRewards);
    yield put(actions.fetchLoyaltySpecialRewardsSuccess(response.data?.data));
  } catch (error: any) {
    yield put(
      actions.fetchLoyaltySpecialRewardsFailure(
        error.message || 'Failed to fetch special rewards'
      )
    );
  }
}

function* updateLoyaltySpecialRewardsSaga(
  action: ReturnType<typeof actions.updateLoyaltySpecialRewardsRequest>
) {
  try {
    const response: any = yield call(api.updateSpecialRewards, action.payload);
    yield put(actions.updateLoyaltySpecialRewardsSuccess(response.data?.data));
    // Re-fetch special rewards so form auto-populates with latest data
    yield put(actions.fetchLoyaltySpecialRewardsRequest());
  } catch (error: any) {
    yield put(
      actions.updateLoyaltySpecialRewardsFailure(
        error.message || 'Failed to update special rewards'
      )
    );
  }
}

export default function* loyaltyProgramSaga() {
  yield takeLatest(T.FETCH_LOYALTY_PROGRAM_REQUEST, fetchLoyaltyProgramSaga);
  yield takeLatest(T.CREATE_LOYALTY_PROGRAM_REQUEST, createLoyaltyProgramSaga);
  yield takeLatest(T.UPDATE_LOYALTY_PROGRAM_REQUEST, updateLoyaltyProgramSaga);
  yield takeLatest(T.UPDATE_LOYALTY_PROGRAM_STATUS_REQUEST, updateLoyaltyProgramStatusSaga);
  yield takeLatest(
    T.FETCH_LOYALTY_SPECIAL_REWARDS_REQUEST,
    fetchLoyaltySpecialRewardsSaga
  );
  yield takeLatest(
    T.UPDATE_LOYALTY_SPECIAL_REWARDS_REQUEST,
    updateLoyaltySpecialRewardsSaga
  );
}
